// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/collectRailContent.test.ts
import {
  type AskPunttChat,
  type AskPunttMessage,
} from "@mg/schemas/src/christo/askPuntt";
import {
  type AskPunttProject,
  type AskPunttProjectInstruction,
} from "@mg/schemas/src/christo/askPunttProject";
import {
  defaultAskPunttConnectors,
  defaultAskPunttSkills,
} from "@mg/schemas/src/commons/enterprise";

import { groupRailCitations } from "./groupRailCitations";
import { isReviewStartUserMessage } from "./isReviewStartUserMessage";
import {
  type AskV2RailConnector,
  type AskV2RailContent,
  type AskV2RailDocument,
  type AskV2RailFinding,
  type AskV2RailMemory,
  type AskV2RailMemoryScope,
  type AskV2RailOutputItem,
  type AskV2RailProgressItem,
  type AskV2RailSkill,
} from "./types";

import {
  selectFocusedCitedClaims,
  threadCitationNumbers,
} from "../../components/ask-puntt-cited-claims";
import { chatsCitedInText } from "../../components/ask-puntt-review/claimCitations";
import { type StreamingVerifiedFinding } from "../../hooks/useAskPunttStream";
import { type GetProjectReviewsResponse } from "../../services/askPunttProjectFeed.types";
import { matchToolToConnectorId } from "../../utils/askPunttKnownConnectorTools";
import {
  projectReferenceDocumentRows,
  referenceRowDisplayName,
} from "../../utils/askPunttProjectReferenceDocuments";
import {
  collectMemoryCitations,
  numberMemoryCitations,
} from "../../utils/memoryCitationFields";
import {
  STREAM_MESSAGE_PREFIX,
  predatesQuestion,
  runningTurnReviewId,
  runningTurnUserMessageId,
} from "../transport/runningTurn";

const CONNECTOR_NAME_BY_ID = new Map(
  defaultAskPunttConnectors.map((connector) => [
    connector.connectorId,
    connector.name,
  ]),
);

const SKILL_NAME_BY_ID = new Map(
  defaultAskPunttSkills.map((skill) => [skill.skillId, skill.name]),
);

type ReviewListCardMeta = {
  title?: string;
  thumbnailUrl?: string | null;
  versionNumber?: number;
  aiCommentCount?: number;
  humanCommentCount?: number;
};

function reviewListCardMetaFromMessage(
  message: AskPunttMessage,
  reviewId: string,
): ReviewListCardMeta | null {
  // warholComponentsForChat does not dedupe atoms, so one message can carry
  // several review-list-cards. Collect them all before choosing, otherwise an
  // exact match sitting after an ambiguous pair is never compared.
  const cards: { id?: string; meta: ReviewListCardMeta }[] = [];
  for (const raw of message.components ?? []) {
    if (raw.componentId !== "ask-puntt.chat-atom.review-list-card") continue;
    const payload = raw.payload as {
      id?: string;
      name?: string;
      thumbnailUrl?: string;
      versionNumber?: number | null;
      aiCommentCount?: number;
      humanCommentCount?: number;
    };
    cards.push({
      id: payload.id,
      meta: {
        title: payload.name?.trim() || undefined,
        thumbnailUrl: payload.thumbnailUrl || undefined,
        versionNumber: payload.versionNumber ?? undefined,
        aiCommentCount: payload.aiCommentCount,
        humanCommentCount: payload.humanCommentCount,
      },
    });
  }

  // Pass 1: an exact id match always wins, wherever it sits in the list.
  const exact = cards.find((card) => card.id === reviewId);
  if (exact) return exact.meta;

  // Pass 2: ambiguity only decides the fallback — keep the sole card on this
  // message, and give up once two or more cards could be the one.
  // why: warhol invents payload.id before christo rewrites it to viewReviewId
  return cards.length === 1 ? cards[0].meta : null;
}

function hasCanvas(message: AskPunttMessage): boolean {
  const blocks = message.canvasContent?.blocks;
  return (
    (Array.isArray(blocks) && blocks.length > 0) ||
    message.canvasLoading === true
  );
}

function titleCaseSlug(slug: string): string {
  return slug
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function canvasTitle(message: AskPunttMessage, index: number): string {
  const outputType = message.canvasContent?.outputType;
  if (outputType) return titleCaseSlug(outputType);
  return `Document ${index + 1}`;
}

function collectConnectors(
  messages: readonly AskPunttMessage[],
): AskV2RailConnector[] {
  const seen = new Map<string, AskV2RailConnector>();
  for (const message of messages) {
    for (const step of message.agentSteps ?? []) {
      if (step.type !== "tool_call" && step.type !== "tool_result") continue;
      const toolName =
        step.type === "tool_result"
          ? step.toolName?.trim() || step.tool
          : step.tool;
      const args =
        step.type === "tool_call" && step.args && typeof step.args === "object"
          ? (step.args as Record<string, unknown>)
          : undefined;
      const id = matchToolToConnectorId(toolName, step.displayName ?? "", args);
      if (!id || seen.has(id)) continue;
      seen.set(id, {
        id,
        name: CONNECTOR_NAME_BY_ID.get(id) ?? id,
      });
    }
  }
  return Array.from(seen.values());
}

function collectDocuments(
  files: AskPunttProject["files"] | undefined,
): AskV2RailDocument[] {
  return projectReferenceDocumentRows(files).map((row) => {
    const name = referenceRowDisplayName(row.file, row.version);
    const resolvedType = row.file.type ?? "file";
    const source = row.version.source ?? row.version.key ?? "";
    return {
      id: `${row.file.fileId}-v${row.versionOrdinal}`,
      fileId: row.file.fileId,
      name,
      type: resolvedType,
      versionOrdinal: row.versionOrdinal,
      source,
      showVersionBadge: (row.file.versions?.length ?? 0) > 1,
    };
  });
}

function attachmentDisplayName(attachment: {
  name?: string;
  key: string;
}): string {
  const named = attachment.name?.trim();
  if (named) return named;
  return attachment.key.split("/").pop()?.trim() ?? "";
}

// why: warhol never files feed uploads onto project.files, so Context stayed empty
function collectAttachmentDocuments(
  messages: readonly AskPunttMessage[],
  filed: readonly AskV2RailDocument[],
): AskV2RailDocument[] {
  const seen = new Set<string>();
  for (const document of filed) {
    if (document.source) seen.add(document.source);
    if (document.name) seen.add(document.name);
  }
  const out: AskV2RailDocument[] = [];
  for (const message of messages) {
    for (const attachment of message.attachments ?? []) {
      const name = attachmentDisplayName(attachment);
      const source = attachment.source?.trim() ?? "";
      if (!name || seen.has(source) || seen.has(name)) continue;
      if (source) seen.add(source);
      seen.add(name);
      out.push({
        id: `attachment-${attachment.key}`,
        fileId: attachment.key,
        name,
        type: attachment.type,
        versionOrdinal: 1,
        source,
        showVersionBadge: false,
      });
    }
  }
  return out;
}

// why-not: no chat memory tier — AskPunttProjectInstruction.scope is project|enterprise only; chat-scoped lists are not on GET project/chat yet.
function collectMemories(
  instructions: readonly AskPunttProjectInstruction[] | undefined,
): AskV2RailMemory[] {
  const byScope = new Map<AskV2RailMemoryScope, string[]>();
  for (const instruction of instructions ?? []) {
    if (instruction.revokedAt != null) continue;
    const text = instruction.text?.trim();
    if (!text) continue;
    const scope = instruction.scope;
    if (scope !== "project" && scope !== "enterprise") continue;
    const bucket = byScope.get(scope) ?? [];
    bucket.push(text);
    byScope.set(scope, bucket);
  }
  const memories: AskV2RailMemory[] = [];
  for (const scope of ["project", "enterprise"] as const) {
    const texts = byScope.get(scope);
    if (texts?.length) {
      memories.push({ scope, text: texts.join(" ") });
    }
  }
  return memories;
}

/**
 * `chat.detectedSkill` is persisted by rembrandt as a skill *id*
 * (`compliance_review`, `custom_<uuid>`) — never a display label. Rendering it
 * verbatim leaks raw slugs and UUIDs into the rail chip, so only accept values
 * that cannot be an id (it differs from the workflow type and is not
 * slug-shaped).
 */
function detectedSkillLabel(
  detectedSkill: string | null | undefined,
  workflowType: string,
): string | undefined {
  const trimmed = detectedSkill?.trim();
  if (!trimmed || trimmed === workflowType) return undefined;
  if (/^[a-z0-9][a-z0-9_-]*$/.test(trimmed)) return undefined;
  return trimmed;
}

function collectSkills(
  chat?: Partial<Pick<AskPunttChat, "workflowType" | "detectedSkill">> | null,
): AskV2RailSkill[] {
  if (!chat?.workflowType || chat.workflowType === "general") return [];

  const workflowType = chat.workflowType;
  const label = detectedSkillLabel(chat.detectedSkill, workflowType);
  if (workflowType.startsWith("custom_")) {
    return [{ id: workflowType, name: label ?? "Custom skill" }];
  }

  const skillId =
    workflowType === "project" ? "project_workflow" : workflowType;
  const name =
    SKILL_NAME_BY_ID.get(skillId) ?? label ?? titleCaseSlug(workflowType);
  return [{ id: skillId, name }];
}

/** Default empty review state — not worth a progress rail section on its own. */
function isMeaningfulProgressStatus(
  status: string | null | undefined,
): boolean {
  const normalized = status?.trim().toLowerCase();
  return Boolean(normalized && normalized !== "idle");
}

function hasUserCommittedReviewStart(
  messages: readonly AskPunttMessage[],
): boolean {
  return messages.some(
    (message) =>
      message.role === "user" &&
      isReviewStartUserMessage(message.content ?? ""),
  );
}

type ReviewProgressChatInput = Pick<
  AskPunttChat,
  "reviewStatus" | "reviewStages"
> &
  Partial<Pick<AskPunttChat, "workflowType" | "reviewIds" | "agent">>;

function isReviewItemStillInFlight(status: string | undefined): boolean {
  return status?.trim().toLowerCase() === "in_review";
}

// why: exported — the version picker in AskV2ProjectPage filters on the SAME
// "openable" rule the rail uses; a second inline copy is the rail-vs-picker
// drift this predicate exists to close.
export function isReviewItemFinished(item: {
  status?: string;
  aiCommentCount?: number;
}): boolean {
  if (!isReviewItemStillInFlight(item.status)) return true;
  // why: listReviews can lag isRunningAIReview; comments on the board mean the
  // AI pass finished even when status is still in_review.
  return (item.aiCommentCount ?? 0) > 0;
}

/** True once any review in the list has landed at least one AI finding. */
export function hasReviewFindings(
  reviews?: GetProjectReviewsResponse | null,
): boolean {
  return (reviews?.reviews ?? []).some(
    (item) => (item.aiCommentCount ?? 0) > 0,
  );
}

function inReviewIdsFromApi(
  reviews?: GetProjectReviewsResponse | null,
): Set<string> {
  const ids = new Set<string>();
  for (const item of reviews?.reviews ?? []) {
    if (!isReviewItemStillInFlight(item.status)) continue;
    if ((item.aiCommentCount ?? 0) > 0) continue;
    ids.add(item.id);
    if (item.ticketId) ids.add(item.ticketId);
  }
  return ids;
}

function lastUserReviewStartIndex(
  messages: readonly AskPunttMessage[],
): number {
  let lastIndex = -1;
  messages.forEach((message, index) => {
    if (
      message.role === "user" &&
      isReviewStartUserMessage(message.content ?? "")
    ) {
      lastIndex = index;
    }
  });
  return lastIndex;
}

function hasFinishedReviewInMessages(
  messages: readonly AskPunttMessage[],
  reviews?: GetProjectReviewsResponse | null,
  /**
   * The Start line this walk is about, when the caller holds the whole thread.
   *
   * The slice it is usually handed (`messagesAfterLatestReviewStart`) is the
   * cycle the review belongs to, and it contains no Start line of its own — so
   * the guard below needs the row from the caller. See `predatesQuestion`.
   */
  since?: { createdAt?: unknown },
): boolean {
  const inReviewIds = inReviewIdsFromApi(reviews);
  const reviewStartIndex = lastUserReviewStartIndex(messages);
  // The Start line's own row, for the same out-of-order guard the running-turn
  // rules use: an answer mirrored below a Start line sent since carries the
  // PREVIOUS review's card and `viewReviewId`, and reading it as this cycle's
  // finish is what pinned the email CTA up over a review that had only started
  // (see `predatesQuestion`). This walk is reached FIRST —
  // `isReviewInPlaceForEmailNotification` asks `hasCompletedReviewEvidence`
  // before `isWarholReviewTurnAnswered`, and `getReviewProgressMode` opens with
  // the same predicate — so without it the guard on that later call would never
  // be the one that decided.
  const start = reviewStartIndex >= 0 ? messages[reviewStartIndex] : undefined;

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.role !== "assistant") continue;
    if (reviewStartIndex >= 0 && index < reviewStartIndex) break;
    if (start && predatesQuestion(message, start)) continue;
    if (since && predatesQuestion(message, since)) continue;

    for (const raw of message.components ?? []) {
      if (raw.componentId !== "ask-puntt.chat-atom.review-list-card") continue;
      const payload = raw.payload as {
        id?: string;
        aiCommentCount?: number;
      };
      const cardId = payload.id?.trim();
      if (!cardId || inReviewIds.has(cardId)) continue;
      if ((payload.aiCommentCount ?? 0) > 0) return true;
    }

    const reviewId = message.viewReviewId?.trim();
    if (!reviewId || inReviewIds.has(reviewId)) continue;
    if (message.status !== "pending") return true;
  }

  return false;
}

function hasActiveReviewWithoutComments(
  reviews?: GetProjectReviewsResponse | null,
): boolean {
  return (reviews?.reviews ?? []).some(
    (item) =>
      isReviewItemStillInFlight(item.status) &&
      (item.aiCommentCount ?? 0) === 0,
  );
}

function hasOnlyFinishedReviews(
  reviews?: GetProjectReviewsResponse | null,
): boolean {
  const reviewList = reviews?.reviews ?? [];
  if (reviewList.length === 0) return false;
  return reviewList.every(isReviewItemFinished);
}

function hasStaleInReviewWithComments(
  reviews?: GetProjectReviewsResponse | null,
): boolean {
  return (reviews?.reviews ?? []).some(
    (item) =>
      isReviewItemStillInFlight(item.status) && (item.aiCommentCount ?? 0) > 0,
  );
}

/**
 * The thread from the newest Start review onwards — the cycle now running.
 *
 * Exported because the plan rail needs the same cut: a chat's SECOND review is
 * already `running` before that turn has written its own `write_plan`, and
 * reading the newest plan anywhere in the thread showed the previous review's
 * fully-ticked checklist as if it belonged to the one that just started.
 * A thread with no Start review at all is returned whole.
 */
export function messagesAfterLatestReviewStart(
  messages: readonly AskPunttMessage[],
): readonly AskPunttMessage[] {
  const startIndex = lastUserReviewStartIndex(messages);
  return startIndex >= 0 ? messages.slice(startIndex + 1) : messages;
}

/**
 * The messages belonging to the turn running now, or null once it settles.
 *
 * The running-turn marker wins over the last user row because a follow-up can
 * be queued below an earlier turn's pending placeholder. The slice stops at
 * the next user row so a later turn's plan cannot replace the active one. A
 * stream snapshot explicitly keyed to this turn is included even when the
 * overlay appended it after a queued user row.
 */
export function messagesOfRunningTurn(
  messages: readonly AskPunttMessage[],
): readonly AskPunttMessage[] | null {
  const turnUserId = runningTurnUserMessageId(messages);
  if (!turnUserId) return null;

  const start = messages.findIndex((message) => message?._id === turnUserId);
  if (start < 0) return null;

  let end = messages.length;
  for (let index = start + 1; index < messages.length; index += 1) {
    if (messages[index]?.role === "user") {
      end = index;
      break;
    }
  }
  const turn = messages.slice(start, end);
  const liveSnapshot = messages.find(
    (message) => message?._id === `${STREAM_MESSAGE_PREFIX}${turnUserId}`,
  );
  return liveSnapshot && !turn.includes(liveSnapshot)
    ? [...turn, liveSnapshot]
    : turn;
}

/**
 * The thread's newest turn: the person's message that asked for it, and every
 * row after it.
 *
 * `messagesOfRunningTurn` answers "is a turn in flight, and which" and returns
 * null the moment the answer lands. This answers the other question the rail
 * asks once nothing is running -- "which turn was that" -- so a settled rail
 * shows the plan of the turn that just ended rather than the newest plan
 * anywhere in the review cycle, which belongs to a PREVIOUS turn.
 *
 * Measured, gate-2 run-1: a follow-up that never planned ran t=1314-1324 and
 * showed only "Getting started". At t=1325 -- the tick its turn ended -- the
 * rail switched to the nutrition plan of the turn before it and held it, and it
 * was still there at the t=1438 refresh. The plan it picked up came from the
 * cycle slice, which the follow-up's own turn never leaves.
 */
export function messagesOfLastTurn(
  messages: readonly AskPunttMessage[] | undefined,
): readonly AskPunttMessage[] | null {
  const rows = messages ?? [];
  for (let index = rows.length - 1; index >= 0; index -= 1) {
    const message = rows[index];
    if (!message || message.hidden || message.role !== "user") continue;
    return rows.slice(index);
  }
  // No person's message to start from, so the whole slice is the turn. That is
  // what every caller answered before this existed -- the cycle slice a thread
  // with no "Start review" in it reduces to -- and it keeps a plan that arrives
  // without its question (an overlay row, a partial payload) on the rail.
  return rows.length > 0 ? rows : null;
}

function hasCompletedReviewEvidence(input: {
  chat?: ReviewProgressChatInput | null;
  messages?: readonly AskPunttMessage[];
  reviews?: GetProjectReviewsResponse | null;
}): boolean {
  if (hasActiveReviewWithoutComments(input.reviews)) return false;

  const messages = input.messages ?? [];
  // One scan for the Start line, used for both the slice and the guard:
  // `messagesAfterLatestReviewStart` computes the same index internally, and
  // this predicate is already the third full walk of this array.
  const startIndex = lastUserReviewStartIndex(messages);
  const afterStart =
    startIndex >= 0 ? messages.slice(startIndex + 1) : messages;

  if (
    hasFinishedReviewInMessages(
      afterStart,
      input.reviews,
      startIndex >= 0 ? messages[startIndex] : undefined,
    )
  ) {
    return true;
  }

  // Stale listReviews status: in_review with comments landed means finished.
  if (hasStaleInReviewWithComments(input.reviews)) return true;

  // Hydration on revisit: history/reviews can arrive before the full thread is
  // scanned; trust an all-finished reviews API only when no Start review cycle
  // is waiting on assistant output yet.
  if (lastUserReviewStartIndex(messages) < 0) {
    return hasOnlyFinishedReviews(input.reviews);
  }

  // Start review stays in history forever; a stale IN REVIEW chat status with
  // an all-finished reviews API means the cycle already completed on refresh.
  if (
    hasOnlyFinishedReviews(input.reviews) &&
    isMeaningfulProgressStatus(input.chat?.reviewStatus)
  ) {
    return true;
  }

  return false;
}

/**
 * Whether the review the thread last asked for ended without running at all.
 *
 * A review that could not run leaves one terminal assistant message carrying
 * `reviewFailed` (`_save_review_failed_message` in rembrandt): no review
 * document, no findings, nothing for the rail to finish on. That message is
 * the only evidence the turn is over — the "Start review" user message stays
 * in the thread forever, and structure alone cannot separate a dead review
 * from a live one, because a healthy run also leaves a ticket-stamped
 * assistant reply behind.
 *
 * This is rembrandt's signal. On a rembrandt chat the review runs after the
 * reply, so a turn without the marker keeps today's behaviour exactly. A
 * warhol chat never writes it — see `isWarholReviewTurnAnswered`, which is
 * checked first and ends the row on the turn's own answer.
 *
 * Only messages after the latest Start line count: a chat that failed once and
 * was reviewed again must not read as failed.
 */
function hasTerminalReviewFailure(
  messages: readonly AskPunttMessage[],
): boolean {
  const startIndex = lastUserReviewStartIndex(messages);
  if (startIndex < 0) return false;
  return messages
    .slice(startIndex + 1)
    .some(
      (message) =>
        message.role === "assistant" && message.reviewFailed === true,
    );
}

/**
 * Whether a warhol chat's latest Start review has been answered.
 *
 * On a warhol chat the review IS the turn: it rides the turn's answer, and
 * christo lands that answer only once the turn is over -- a review, a clean
 * "nothing to change", or the reply warhol settles a failed turn with
 * (`_settle_failed_turn`, which carries `error`, never rembrandt's
 * `reviewFailed`). Until then the only assistant row after the Start line is
 * the `pending` placeholder that shows the turn's calls. So an answered Start
 * line is the end of the cycle whatever the answer said, and without this a
 * failed or 0-finding turn -- neither writes a review document -- left the
 * Start line holding the rail on "running" for the life of the chat.
 *
 * Position alone is not enough, because the row after the Start line is not
 * always the review turn's: an answer is mirrored into the transcript when a
 * READ lands it, so a turn asked BEFORE the review can land its own answer below
 * a Start line sent since. A row that predates the Start line answers it no more
 * than it answers anything else, and it must not end the cycle.
 */
function isWarholReviewTurnAnswered(
  chat: ReviewProgressChatInput | null | undefined,
  messages: readonly AskPunttMessage[],
): boolean {
  if (chat?.agent !== "warhol") return false;
  const startIndex = lastUserReviewStartIndex(messages);
  if (startIndex < 0) return false;
  const start = messages[startIndex];
  if (!start) return false;
  return messages.slice(startIndex + 1).some(
    (message) =>
      message.role === "assistant" &&
      message.status !== "pending" &&
      // A row that PREDATES the Start line cannot be its answer. christo
      // mirrors an answer into the transcript when a read lands it — not
      // when warhol wrote it — so the turn BEFORE the review can land its
      // answer below the `**Start review**` message. Measured on dev, chat
      // `6ad19876037e11ef481e9561` (2026-10-16): the preflight card
      // (`createdAt` 03:23:16.551) sat under the Start line sent at
      // 03:23:17.023, so this answered "the review is over" for the whole of
      // a running review — the Progress section read `idle` and the "email
      // me when reviews are done" CTA never appeared in any of the run's 35
      // frames. See `predatesQuestion`.
      !predatesQuestion(message, start),
  );
}

/**
 * Whether the thread carries christo's placeholder for a warhol turn that is
 * still running.
 *
 * `upsertWarholTurnPlaceholder` appends a `pending` assistant row to a warhol
 * chat the moment the running turn has a tool call to show, and replaces that
 * row in place when the answer lands. The newest VISIBLE ASSISTANT row is
 * therefore the one signal that says a review is running RIGHT NOW, whatever
 * started it.
 *
 * The newest assistant row, not the thread's tail: a follow-up queued while the
 * review ran sits BELOW the placeholder, so the tail of a running turn is the
 * person's own message. `runningTurnUserMessageId` reads the same thread the
 * same way — it walks up to the newest `pending` row rather than reading the
 * last row — and `messagesOfRunningTurn` still names that turn. Reading only
 * the tail here made the two disagree: the moment she typed again mid-review
 * the mode flipped to `idle`, and `useAskV2PlanProgress` returns no items on
 * `idle`, so the plan those helpers had just held on to was dropped and the
 * rail went blank for the rest of the turn. User rows above the newest
 * assistant row are that turn's own question, which never ends a turn either.
 *
 * Every other in-flight signal is about how the review was ASKED for —
 * `reviewStatus`, `reviewStages`, an in-review row on the reviews API, or the
 * `**Start review**` message the preflight card writes. A warhol turn started
 * any other way (christo's API, an integration) carries none of them, so the
 * rail read `idle` and drew NOTHING for the whole review: `useAskV2PlanProgress`
 * returns no items, so `mergeRailProgress` leaves the shell empty. The Story 4
 * acceptance ran exactly that shape and saw an empty rail until `reviewIds`
 * landed near the end, which flips the mode to `completed` and draws the plan
 * already ticked.
 *
 * Only warhol: a rembrandt turn's pending row is a different machine, and
 * `hasCompletedReviewEvidence` has already ruled out a cycle the reviews API
 * says is over. The stale/failed guards do NOT run first — they read markers a
 * PREVIOUS turn left behind, and this row is the turn running now.
 */
function hasRunningWarholTurn(
  chat: ReviewProgressChatInput | null | undefined,
  messages: readonly AskPunttMessage[],
): boolean {
  if (chat?.agent !== "warhol") return false;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (!message || message.hidden || message.role !== "assistant") continue;
    return message.status === "pending";
  }
  return false;
}

export function isReviewInFlight(input: {
  chat?: ReviewProgressChatInput | null;
  reviews?: GetProjectReviewsResponse | null;
  messages?: readonly AskPunttMessage[];
}): boolean {
  if (hasCompletedReviewEvidence(input)) return false;

  // Ahead of BOTH end-of-turn markers below, because each of them is about a
  // turn that ENDED and christo's `pending` placeholder is about the turn
  // running RIGHT NOW. A warhol chat outlives its review cycle: she reads the
  // answer and asks again, and that follow-up turn gets a placeholder of its
  // own while carrying none of the how-it-was-asked-for signals -- no second
  // `**Start review**` line, no `reviewStatus`, no `reviewStages`, and no
  // reviews-API row, because a turn that found nothing writes no review
  // document at all (`persistWarholReview`). Read after the answered-Start
  // check, the Start line that the FIRST turn answered reported the chat
  // `idle` for the rest of its life, and `useAskV2PlanProgress` returns no
  // items on `idle` -- so every turn after the first drew the blank rail this
  // placeholder signal exists to end.
  if (hasRunningWarholTurn(input.chat, input.messages ?? [])) return true;

  // An answered warhol turn is over, and a status nothing clears must not
  // outlive it — same reason as the failure marker below.
  if (isWarholReviewTurnAnswered(input.chat, input.messages ?? [])) {
    return false;
  }

  // Ahead of every in-flight signal below, because none of them can outlive a
  // review that died. `_save_review_failed_message` unsets `reviewStages` /
  // `reviewStream` / `reviewEvidence` on the chat but NOT `reviewStatus`: that
  // is a separate best-effort write from another workflow
  // (`clear_review_status`), swallowed on error and skipped outright when the
  // worker dies with the review. A failed turn also leaves no review row for
  // the reviews API, so `hasCompletedReviewEvidence` can never rescue it
  // either — `hasOnlyFinishedReviews` is false on an empty list. Checked after
  // the stale signals, a leftover "IN REVIEW" kept the rail spinning for the
  // rest of the chat's life, which is the exact hang this marker was added to
  // end.
  if (hasTerminalReviewFailure(input.messages ?? [])) return false;

  if (hasKickedOffActiveReview(input)) return true;
  if (hasRunningWarholTurn(input.chat, input.messages ?? [])) return true;
  return false;
}

type ReviewProgressInput = {
  chat?: ReviewProgressChatInput | null;
  reviews?: GetProjectReviewsResponse | null;
  messages?: readonly AskPunttMessage[];
};

/**
 * A review the thread has actually started, still inside an open cycle.
 *
 * Callers apply the completed / answered / failed gates first. This is every
 * in-flight signal except a pending warhol assistant row.
 */
function hasKickedOffActiveReview(input: ReviewProgressInput): boolean {
  if (isMeaningfulProgressStatus(input.chat?.reviewStatus)) return true;
  if ((input.chat?.reviewStages?.length ?? 0) > 0) return true;
  if (
    (input.reviews?.reviews ?? []).some(
      (item) =>
        isReviewItemStillInFlight(item.status) &&
        (item.aiCommentCount ?? 0) === 0,
    )
  ) {
    return true;
  }
  // why: the Start review user message stays in the thread forever; once the
  // reviews API shows a finished review, that message must not keep progress
  // running after a page refresh. Callers apply hasCompletedReviewEvidence
  // and the failure marker first so a finished or failed cycle wins.
  return hasUserCommittedReviewStart(input.messages ?? []);
}

// why: isReviewInFlight is true for any pending warhol turn so the rail can draw; this email CTA waits until a review itself is kicked off
export function isReviewInPlaceForEmailNotification(
  input: ReviewProgressInput,
): boolean {
  if (hasCompletedReviewEvidence(input)) return false;
  if (isWarholReviewTurnAnswered(input.chat, input.messages ?? [])) {
    return false;
  }
  // why: warhol never writes reviewFailed, so the answered guard above cannot end a dead rembrandt cycle
  if (hasTerminalReviewFailure(input.messages ?? [])) return false;
  return hasKickedOffActiveReview(input);
}

export type AskV2ReviewProgressMode = "idle" | "running" | "completed";

export function getReviewProgressMode(input: {
  chat?: ReviewProgressChatInput | null;
  reviews?: GetProjectReviewsResponse | null;
  messages?: readonly AskPunttMessage[];
}): AskV2ReviewProgressMode {
  // why: Start review and stale reviewStatus persist in history after a review
  // finishes; finished reviews must win on refresh so the rail shows all done.
  if (hasCompletedReviewEvidence(input)) {
    return "completed";
  }

  if (isReviewInFlight(input)) return "running";

  const reviewList = input.reviews?.reviews ?? [];
  if (reviewList.some((item) => isReviewItemFinished(item))) {
    return "completed";
  }
  if ((input.chat?.reviewIds?.length ?? 0) > 0) {
    return "completed";
  }

  return "idle";
}

/**
 * The order the live findings are drawn in: what she must act on, first.
 *
 * Three ranks, not four. warhol ranks critical above major, but both are
 * "cannot ship without this" (`isRequired` in `warholReview.ts` is exactly that
 * pair), and the rail is a glanceable list rather than the review's own
 * severity-sorted comment array — so the urgent pair leads and `minor` and
 * `check` follow. Unknown severities rank lowest rather than dropping out: a
 * finding warhol published is a finding, whatever its ladder says.
 */
const RAIL_FINDING_RANK: Record<string, number> = {
  critical: 3,
  major: 2,
  minor: 1,
  check: 0,
};

// why: comment cards already say High/Medium/Low confidence, so a live row's status is the fix and only when that sentence is not the observation.
export function liveFindingInlineStatus(
  finding: unknown,
  description: string,
): string {
  if (typeof finding !== "object" || finding === null) return "";
  const record = finding as Record<string, unknown>;
  const action =
    typeof record.required_action === "string"
      ? record.required_action.replace(/\s+/g, " ").trim()
      : "";
  // Stryker disable next-line ConditionalExpression: its `||` parent means `action === ""` only gets `-> false`, which is equivalent -- that branch returns "", which is the action itself. The whole check `-> true` blanks every status, which "keeps a distinct fix as the live finding's inline status" kills
  if (action === "" || action === description) return "";
  return action;
}

// why: more than five in-progress comments collapse behind Show more; five or fewer stay fully shown.
export const IN_PROGRESS_FINDINGS_SHOWN = 5;

export function inlineFindingAria(
  status: string | undefined,
  description: string,
): string {
  if (status == null || status === "") return description;
  // why: required_action is usually already a sentence, so a second period stutters the name.
  const glue = /[.!?]$/.test(status) ? " " : ". ";
  return `${status}${glue}${description}`;
}

/** warhol's wording for a finding, short enough for a rail row. */
function liveFindingDescription(finding: unknown): string {
  if (typeof finding !== "object" || finding === null) return "";
  const record = finding as Record<string, unknown>;
  const words =
    typeof record.observation === "string"
      ? record.observation
      : typeof record.required_action === "string"
        ? record.required_action
        : "";
  return words.replace(/\s+/g, " ").trim();
}

/**
 * The findings a running review has verified so far, worst first.
 *
 * Reads the stream frame the transport already holds — the same socket that
 * feeds the per-check rows — and joins each one to the review the pending row
 * points at, so a row becomes openable the moment christo has written the
 * provisional review. The join itself is by intent id at the other end
 * (`AskV2ReviewWorkspace` matches the live row to its saved comment); here the
 * only thing to find is the review id, which lives on the row.
 *
 * Rows without an intent id or without wording are dropped: they have nothing
 * to merge on and nothing to say, and a row the person cannot act on is worse
 * than the row not appearing.
 */
export function collectLiveFindings(
  messages: readonly AskPunttMessage[],
  verifiedFindings: readonly StreamingVerifiedFinding[] | undefined,
): AskV2RailFinding[] | null {
  const findings = verifiedFindings ?? [];
  if (findings.length === 0) return null;

  // The RUNNING turn's review, when christo has written it yet. Scoped on
  // purpose: `viewReviewId` reaches a message row only once the provisional
  // write has run and a later history poll has returned it, so between warhol
  // publishing the first finding and that poll landing the newest row carrying
  // one is the PREVIOUS turn's settled answer — and stamping it here would draw
  // an openable row that opens a different review.
  const reviewId = runningTurnReviewId(messages);

  const rows: AskV2RailFinding[] = [];
  const seen = new Set<string>();
  for (const entry of findings) {
    const intentId = entry?.intentId;
    if (!intentId || seen.has(intentId)) continue;
    const description = liveFindingDescription(entry.finding);
    if (!description) continue;
    seen.add(intentId);
    const status = liveFindingInlineStatus(entry.finding, description);
    rows.push({
      intentId,
      severity: entry.severity ?? "",
      description,
      ...(status !== "" ? { status } : {}),
      ...(reviewId ? { reviewId } : {}),
    });
  }
  if (rows.length === 0) return null;

  return rows
    .map((row, index) => ({ row, index }))
    .sort(
      (left, right) =>
        (RAIL_FINDING_RANK[right.row.severity] ?? -1) -
          (RAIL_FINDING_RANK[left.row.severity] ?? -1) ||
        left.index - right.index,
    )
    .map((entry) => entry.row);
}

/**
 * Whether `groupRailCitations` keeps anything the rail can put on screen.
 *
 * The rail draws a citation through `groupRailCitations`, which keeps rule
 * chips, related rules, cited chats, cited memories and past-review findings
 * and drops everything else — so this, not the raw list's length, is what
 * "this answer has evidence" means here.
 */
function hasDrawableCitations(citations: readonly unknown[] | undefined) {
  const grouped = groupRailCitations(citations);
  return (
    grouped.claims.length > 0 ||
    grouped.related.length > 0 ||
    grouped.chats.length > 0 ||
    grouped.memories.length > 0 ||
    grouped.findings.length > 0
  );
}

/**
 * The answer whose evidence the rail shows: the newest one it can DRAW.
 *
 * `selectFocusedCitedClaims` stops at the newest answer that cited ANYTHING,
 * and "anything" includes the provenance-free subgraph
 * `KnowledgeGraphRelationshipsTool` persists on its own — a claim's
 * claim->claim / claim->requirement edges with no rule, chat, memory or
 * finding behind them, which `groupRailCitations` drops to the last row. An
 * answer carrying only those reads as cited to the V1 rule while the rail can
 * draw none of it, so the picker parked on it and `hasRailContent` closed the
 * whole 320px rail behind it on a chat whose only rail content was its
 * evidence. Scrolling back to one hit the same wall through the other door,
 * with the "From an earlier answer" banner left drawn over an empty body.
 *
 * Both sides of that promise — "an answer that cited nothing falls back to the
 * latest, so scrolling past one never empties the panel" — have to read
 * "cited nothing THIS RAIL CAN DRAW", so an undrawable answer is passed over
 * exactly like one that cited nothing at all. The V1 rule itself is untouched:
 * this re-asks it with the undrawable answers taken out of the thread, rather
 * than re-implementing which answer wins. The scan is bounded by
 * construction: every pass drops the row it just rejected.
 */
function selectDrawableCitedClaims(
  messages: readonly AskPunttMessage[],
  focusedMessageId?: string | null,
): ReturnType<typeof selectFocusedCitedClaims> {
  let rows = messages;
  // why: Skip an answer whose evidence the rail cannot draw, so the panel
  // keeps the newest answer a reader can actually read. The picker used to
  // stop at the newest answer that cited anything at all — a reader who asked
  // a follow-up lost the rule chips the previous answer stood on, and on a
  // chat whose only rail content was that evidence the whole rail swung shut
  // and showed nothing at all.
  for (let pass = 0; pass <= messages.length; pass += 1) {
    const picked = selectFocusedCitedClaims(rows, focusedMessageId);
    if (!picked || hasDrawableCitations(picked.citations)) return picked;
    rows = rows.filter((message) => message._id !== picked.messageId);
  }
  return null;
}

export function collectRailContent(input: {
  project?: Pick<
    AskPunttProject,
    "description" | "context" | "upcomingWork" | "files" | "instructions"
  > | null;
  chat?:
    | (Pick<
        AskPunttChat,
        "reviewStatus" | "reviewChecklist" | "reviewStages" | "projectData"
      > &
        Partial<
          Pick<
            AskPunttChat,
            "workflowType" | "detectedSkill" | "reviewIds" | "agent"
          >
        >)
    | null;
  messages?: readonly AskPunttMessage[];
  reviews?: GetProjectReviewsResponse | null;
  /**
   * The live `verifiedFindings` off the running turn's stream snapshot, or
   * undefined when no review is running. Read straight off the transport rather
   * than off a message: `applyStreamSnapshot` deliberately lays only the fields
   * the thread owns (`agentSteps`, `checkProgress`) onto the row, so this one
   * reaches the rail as its own input and the socket stays single.
   */
  liveFindings?: readonly StreamingVerifiedFinding[] | undefined;
  /**
   * The answer the reader has scrolled to (`useReadingMessage`), or null at
   * the bottom. Its citations replace the latest answer's on the rail.
   */
  focusedCitationMessageId?: string | null;
}): AskV2RailContent {
  const messages = input.messages ?? [];
  const reviews = input.reviews?.reviews ?? [];
  const inReview = reviews.filter(
    (item) =>
      isReviewItemStillInFlight(item.status) &&
      (item.aiCommentCount ?? 0) === 0,
  );
  const finishedReviews = reviews.filter((item) => isReviewItemFinished(item));

  // why: Progress rows come from the turn's own `write_plan` checklist
  // (useAskV2PlanProgress), not from the timers this file used to be paired
  // with; the rows are merged in by the page, so `items` stays empty here.
  const progressMode = getReviewProgressMode(input);

  // Prefer whichever source carries a meaningful (non-"idle") status: the
  // reviews panel can sit at its "idle" default while the chat has already
  // moved to "IN REVIEW", and `??` would let that idle default shadow it.
  const reviewsStatus = input.reviews?.status;
  const chatStatus = input.chat?.reviewStatus ?? undefined;
  const progressStatus = isMeaningfulProgressStatus(reviewsStatus)
    ? reviewsStatus
    : isMeaningfulProgressStatus(chatStatus)
      ? chatStatus
      : (reviewsStatus ?? chatStatus);
  const progress =
    progressMode !== "idle"
      ? {
          status: isMeaningfulProgressStatus(progressStatus)
            ? progressStatus
            : undefined,
          items: [] as AskV2RailProgressItem[],
        }
      : null;

  const outputItems: AskV2RailOutputItem[] = [];
  const seenReviewIds = new Set<string>();
  // In-review reviews render as in-flight progress items, not output cards.
  // Seed their ids so a mirrored assistant message (viewReviewId/viewTicketId)
  // for the same review does not leak a duplicate "Review" card into output.
  for (const item of inReview) {
    seenReviewIds.add(item.id);
    if (item.ticketId) seenReviewIds.add(item.ticketId);
  }
  for (const item of finishedReviews) {
    seenReviewIds.add(item.id);
    if (item.ticketId) seenReviewIds.add(item.ticketId);
    outputItems.push({
      id: item.id,
      kind: "review",
      title: item.title ?? item.name,
      thumbnailUrl: item.thumbnailUrl,
      versionNumber: item.versionNumber,
      aiCommentCount: item.aiCommentCount,
      humanCommentCount: item.humanCommentCount,
    });
  }

  let canvasIndex = 0;
  for (const message of messages) {
    if (message.role !== "assistant") continue;
    const reviewId = message.viewReviewId;
    if (reviewId && !seenReviewIds.has(reviewId)) {
      seenReviewIds.add(reviewId);
      const atomMeta = reviewListCardMetaFromMessage(message, reviewId);
      outputItems.push({
        id: reviewId,
        kind: "review",
        title: atomMeta?.title ?? "Review",
        thumbnailUrl: atomMeta?.thumbnailUrl,
        versionNumber: atomMeta?.versionNumber,
        aiCommentCount: atomMeta?.aiCommentCount,
        humanCommentCount: atomMeta?.humanCommentCount,
        isGenerating: message.status === "pending",
      });
    }
    if (hasCanvas(message)) {
      outputItems.push({
        id: message._id,
        kind: "canvas",
        title: canvasTitle(message, canvasIndex),
        isGenerating: message.canvasLoading === true,
      });
      canvasIndex += 1;
    }
  }

  const output = outputItems.length > 0 ? { items: outputItems } : null;

  const overview =
    input.project?.description?.trim() ||
    input.project?.context?.trim() ||
    input.chat?.projectData?.description?.trim() ||
    "";
  const upcomingWork = input.project?.upcomingWork?.trim() || "";
  const memories = collectMemories(input.project?.instructions);
  const connectors = collectConnectors(messages);
  const filedDocuments = collectDocuments(input.project?.files);
  const documents = [
    ...filedDocuments,
    ...collectAttachmentDocuments(messages, filedDocuments),
  ];
  const skills = collectSkills(input.chat);
  // The answer the reader is on, else the most recent answer that cited
  // something, exactly as the legacy chat's rail picks it
  // (`selectFocusedCitedClaims`): rule chips first, then the graph's
  // relationships behind them. An answer that cited nothing falls back to the
  // latest, so scrolling past one never empties the panel.
  const focusedCitations = selectDrawableCitedClaims(
    messages,
    input.focusedCitationMessageId,
  );
  const latestCitations = input.focusedCitationMessageId
    ? selectDrawableCitedClaims(messages)
    : focusedCitations;
  const citationsFromEarlierAnswer =
    focusedCitations != null &&
    latestCitations != null &&
    focusedCitations.messageId !== latestCitations.messageId;
  const citedClaims = [...(focusedCitations?.citations ?? [])];
  // why: the chips and badges in the thread carry thread-wide numbers
  // (`ThreadCitationNumbersContext`), so a card names its source by the same
  // number whichever answer the rail is showing.
  const citationNumbers = threadCitationNumbers(messages);
  // The M numbers of the saved memories that name a sentence, from the answer's
  // own text and RAW citations: the thread numbers its chips from the same two
  // inputs (`useMessageClaimCitations`), so the card and the chip cannot differ.
  const focusedMessage = focusedCitations
    ? messages.find((message) => message._id === focusedCitations.messageId)
    : undefined;
  const memoryNumbers = new Map(
    numberMemoryCitations(
      typeof focusedMessage?.content === "string"
        ? focusedMessage.content
        : undefined,
      collectMemoryCitations(focusedMessage?.citations),
    ).map((memory) => [memory.id, memory.number] as const),
  );
  const chatNumbers = chatsCitedInText(
    typeof focusedMessage?.content === "string"
      ? focusedMessage.content
      : undefined,
    focusedMessage?.citations,
  );
  // why: once the value is a non-null object, `"ruleId" in citation` and
  // reading `.ruleId` agree, so the `in` check cannot be told apart from the
  // read. A chat number is built from these same rule ids
  // (`chatsCitedInText` skips a claim with none), so it cannot be the only
  // reason the message id below is set.
  const hasRuleClaims = citedClaims.some(
    (citation) =>
      // Stryker disable next-line ConditionalExpression: selectFocusedCitedClaims throws on a null citation before this runs, and every value that arrives is already non-null, so forcing this check true changes nothing
      citation != null &&
      typeof citation === "object" &&
      Boolean((citation as { ruleId?: string }).ruleId),
  );
  // Count the evidence the rail can actually DRAW, not the raw citation list.
  // `AskV2RightRail` renders a citation through `groupRailCitations`, which
  // keeps rule chips, cited chats, cited memories and past-review findings
  // and drops everything else. `KnowledgeGraphRelationshipsTool` persists its subgraph on its own
  // (`tools.py`, KnowledgeGraphRelationshipsTool.execute) without any rule
  // citation beside it, and its edges are routinely claim->claim / claim->
  // requirement -- provenance-free. Counting the raw list there opened a
  // Context section with a heading and an empty body, and on the chat page
  // `hasRailContent` swung the whole 320px rail out for it.
  const hasRenderableCitations = hasDrawableCitations(citedClaims);
  const context =
    overview ||
    upcomingWork ||
    memories.length > 0 ||
    connectors.length > 0 ||
    documents.length > 0 ||
    skills.length > 0 ||
    hasRenderableCitations
      ? {
          overview: overview || undefined,
          upcomingWork: upcomingWork || undefined,
          memories,
          connectors,
          documents,
          skills,
          citedClaims,
          citationNumbers,
          ...(focusedCitations && (memoryNumbers.size > 0 || hasRuleClaims)
            ? {
                citedMessageId: focusedCitations.messageId,
                ...(memoryNumbers.size > 0 ? { memoryNumbers } : {}),
                ...(chatNumbers.size > 0 ? { chatNumbers } : {}),
              }
            : {}),
          ...(citationsFromEarlierAnswer
            ? { citationsFromEarlierAnswer: true }
            : {}),
        }
      : null;

  const findings = collectLiveFindings(messages, input.liveFindings);

  return { progress, output, context, findings };
}

export function hasRailContent(content: AskV2RailContent): boolean {
  // why: live findings are pending comments drawn in the thread, so they must not open an empty Progress pane
  return (
    content.progress != null ||
    content.output != null ||
    content.context != null
  );
}
